import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { comnUtils } from "@/comn/utils";
import { Button, Grid, Group, Layout, Page } from "@/comn/components";
import { useFetch, useForm, useGrid, useStore, useTheme, useResource, usePage, useToast } from "@/comn/hooks";
import { APIS, URLS, SF_ACSS_SRCH, SG_ACSS_LIST } from "@/tz/go/egaz/osci/ststcl/ptl/services/OsciAcssStstclService";
import dayjs from "dayjs";

/*
 * Access Statistics List
 * !== 접속 통계 목록 ==!
 */
export const OSCI0501001Q = (): JSX.Element => {
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { pgeUid, menuId } = usePage({
        auth: ["auth_osci0003"],
    });
    const { theme } = useTheme();
    const navigate = useNavigate();
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!

    useResource({ defaultSchema: [{ area: "comnCd", comnCd: "OSC0088" }] });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        acssSrch: useForm({
            defaultSchema: SF_ACSS_SRCH,
            defaultValues: {
                acssDtStrt: comnUtils.getDate({ m: -1 }),
                acssDtEnd: comnUtils.getDate(),
                // Restore the saved search conditions after applying the initial date range.
                // !== 초기 날짜 범위를 적용한 후 저장된 검색 조건을 복원한다. ==!
                ...pgeStore?.form,
            },
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        acssList: useGrid({
            defaultSchema: SG_ACSS_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        getAcssStstclList: useFetch({
            api: (page = grid.acssList.page) =>
                APIS.getAcssStstclList(form.acssSrch.getValues(), page, grid.acssList.size),
            enabled: comnUtils.isEmpty(form.acssSrch.errors) && form.acssSrch.isSubmitted,
            key: [grid.acssList.page, grid.acssList.size],
            onSuccess: () => {
                setStore(pgeUid, {
                    form: form.acssSrch.getValues(),
                    page: grid.acssList.page,
                    size: grid.acssList.size,
                });
            },
            onError: () => toast.showToast({ type: "error", content: "msg.osc.00043" }), // Failed to select. !== 조회가 실패하였습니다. ==!
        }),
    };

    const render = {
        cell: {
            menuNm: (props: any) => {
                const { rowValues, value } = props;
                const usrId = comnUtils.isEmpty(form.acssSrch.getValue("usrId"))
                    ? ""
                    : `?usrId=${form.acssSrch.getValue("usrId")}`;
                return (
                    <Link
                        to={`${URLS.osci0501002q}/${dayjs(rowValues.acssDt).format("YYYY-MM-DD")}/${
                            rowValues.menuId
                        }${usrId}`}
                    >
                        {value}
                    </Link>
                );
            },
        },
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Click Search Button !== 검색버튼 ==!
        onSrchBtnClick: () => {
            form.acssSrch.handleSubmit(
                () => {
                    grid.acssList.setPage(0);
                    fetch.getAcssStstclList.fetch(0);
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.osc.00003" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                },
            )();
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        // Service Category 는 고정값이므로 수정할 수 없도록 한다.
        if (comnUtils.isEmpty(form.acssSrch.getValue("bsopClsfCd"))) {
            form.acssSrch.setValue("bsopClsfCd", "OSC");
        }
        form.acssSrch.setEditable("bsopClsfCd", false);

        handler.onSrchBtnClick();
    }, [theme]);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.acssSrch.schema.acssDt} controlSize={10} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.acssSrch.schema.bsopClsfCd} />
                            <Group.Control {...form.acssSrch.schema.usrId} />
                        </Group.Row>
                    </Group.Section>
                    <Layout>
                        <Layout.Left>
                            <Button
                                role={"reset"}
                                onClick={() => {
                                    form.acssSrch.reset();
                                    form.acssSrch.setValues({
                                        acssDtStrt: comnUtils.getDate({ m: -1 }),
                                        acssDtEnd: comnUtils.getDate(),
                                        bsopClsfCd: "OSC",
                                        usrId: "",
                                    });
                                }}
                            />
                        </Layout.Left>
                        <Layout.Right>
                            <Button role={"search"} onClick={handler.onSrchBtnClick} />
                        </Layout.Right>
                    </Layout>
                </Group.Body>
            </Group>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Grid
                            {...grid.acssList.grid}
                            data={fetch.getAcssStstclList.data?.acssStstclList}
                            render={render}
                        />
                    </Group.Section>
                </Group.Body>
            </Group>
        </Page>
    );
};
